import { RecurringRequestList, SubMerchantReason } from '@back-office/http';
import { getDatetimeText, removeEmpty } from '@back-office/ui';
import { DateRange } from '@material-ui/pickers';
import endOfDay from 'date-fns/endOfDay';
import { useLocalStore } from 'mobx-react';
import { Instance } from 'mobx-state-tree';
import qs from 'query-string';
import { MouseEvent, useState } from 'react';
import { useMount, useToggle, useUnmount } from 'react-use';
import { Store } from '.';
import useInject from '../../hooks/useInject';
import RecurringRequestListModel from '../../stores/recurring-request.store';

type EnrollmentTypeProps = {
  label: string;
  value: string;
};

type UseViewModelReturn = {
  on: boolean;
  toggle: (val: boolean) => void;
  state: Store;
  recurringRequest: Instance<typeof RecurringRequestListModel>;
  enrollmentTypeDropData: EnrollmentTypeProps[];
  isEnrollmentType: boolean;
};

const useViewModel = (): UseViewModelReturn => {
  const {
    getRecurringsRequestList,
    recurringRequest,
    auth,
    getRecurringsEnrollmentTypeList,
  } = useInject((root) => root);
  const [on, toggle] = useToggle(false);
  const [enrollmentTypeDropData, setEnrollmentTypeDropData] = useState<
    EnrollmentTypeProps[]
  >([]);
  const isEnrollmentType = auth.currentUser?.isEnrollmentType;

  const state = useLocalStore<Store>(() => ({
    date: [null, null],
    openCalendar: false,
    start: 0,
    page: 1,
    count: 20,
    enrollment_id_or_reference_no: '',
    enrollment_type_id: '',
    wallet_id: '',
    full_name: '',
    mobile_no: '',
    status: '',
    isDetail: false,
    recurringId: null,
    get query(): string {
      return qs.stringify(
        removeEmpty({
          page: this.page,
          count: 20,
          enrollment_id_or_reference_no:
            this.enrollment_id_or_reference_no || null,
          wallet_id: this.wallet_id || null,
          full_name: this.full_name || null,
          mobile_no: this.mobile_no || null,
          status: this.status || null,
          enrollment_type_id: this.enrollment_type_id || null,
          merchantID: auth.currentUser.merchantID,
          created_date_from: this.date[0]
            ? getDatetimeText(this.date[0], 'western')
            : null,
          created_date_to: this.date[1]
            ? getDatetimeText(endOfDay(this.date[1]), 'western')
            : null,
        })
      );
    },
    handleOnGo() {
      this.page = 1;
      this.start = 0;
      getRecurringsRequestList(this.query);
    },

    handleOnChangeDatePicker(date: DateRange<Date>) {
      this.date = date;
    },

    handleOnClear(event: MouseEvent<HTMLOrSVGElement>) {
      event.stopPropagation();
      this.date = [null, null];
      this.handleOnGo();
    },
    handleOnClickDatePicker() {
      this.openCalendar = true;
    },
    handleOnCloseDatePicker() {
      this.openCalendar = false;
      this.handleOnGo();
    },

    handleOnChangePage(newPage: number) {
      this.page = newPage;
      getRecurringsRequestList(this.query);
    },
    handleOnClickRow(recurring: RecurringRequestList) {
      this.recurringId = recurring.id;
      this.isDetail = true;
    },
    handleKeySearch(searchValue: string) {
      if (this.enrollment_id_or_reference_no !== searchValue) {
        this.enrollment_id_or_reference_no = searchValue;
        this.handleOnGo();
      }
    },

    handleWalletIdSearch(searchValue: string) {
      if (this.wallet_id !== searchValue) {
        this.wallet_id = searchValue;
        this.handleOnGo();
      }
    },

    handleFullNameSearch(searchValue: string) {
      if (this.full_name !== searchValue) {
        this.full_name = searchValue;
        this.handleOnGo();
      }
    },
    handleMobileNumberSearch(searchValue: string) {
      if (this.mobile_no !== searchValue) {
        this.mobile_no = searchValue;
        this.handleOnGo();
      }
    },
    handleStatusSearch(value: string) {
      if (this.status !== value) {
        this.status = value === 'All' ? '' : value;
        this.handleOnGo();
      }
    },
    handleEnrollmentSearch(value: string) {
      if (this.enrollment_type_id !== value) {
        this.enrollment_type_id = value;
        this.handleOnGo();
      }
    },
    handleOnClickBreadcrumbs() {
      this.recurringId = null;
      this.isDetail = false;
      getRecurringsRequestList(state.query);
    },
  }));

  useMount(async () => {
    getRecurringsRequestList(state.query);

    const res = await getRecurringsEnrollmentTypeList();
    if (res?.length > 0) {
      const formattedData = [
        { label: 'All enrollment type', value: '' }, // Default option
        ...res.map((item: SubMerchantReason) => ({
          label: item.reason,
          value: item.id,
        })),
      ];
      setEnrollmentTypeDropData(formattedData);
    }
  });

  useUnmount(() => {
    recurringRequest.reset();
  });

  return {
    enrollmentTypeDropData,
    on,
    toggle,
    state,
    recurringRequest,
    isEnrollmentType,
  };
};

export default useViewModel;
